import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@finance/context/AuthContext";
import { api } from "@finance/lib/api";
import { useTheme } from "@finance/context/ThemeContext";
import NotificationsBell from "@finance/components/NotificationsBell";
import { ReadOnlyGuard, ForcePasswordChange } from "@finance/components/AccessGuards";
import {
  SquaresFour, ListChecks, ListBullets, Receipt, Money, UsersThree,
  Package, Tray, Crown, SignOut, Truck, FileText, Percent, Bank, ChartBar, BookOpen, Trash, ClockCountdown, CurrencyCircleDollar,
  UsersFour, Barcode, Coins, LinkSimple, TreeStructure, ShieldWarning, ClipboardText, CaretDown, EnvelopeSimple, Wallet, Lock, Scales,
  Export, UserCircle, Moon, Sun, HandCoins, Gear, Hash, ShieldCheck, NotePencil, CalendarBlank, Warning, List, X,
} from "@phosphor-icons/react";

const HR_NAV_ITEMS = [
  { to: "/hr/administrative", label: "Administrative", icon: Gear },
  { to: "/hr/todo", label: "To-Do List", icon: ListChecks },
  { to: "/hr/employee", label: "Employee", icon: UsersThree },
  { to: "/hr/compliance", label: "Compliance", icon: ShieldWarning },
  { to: "/hr/business-development", label: "Business Development", icon: Coins },
];

const GROUPS = [
  { type: "item", to: "/", label: "Dashboard", icon: SquaresFour, end: true },
  { type: "group", label: "Actions", icon: ListChecks, items: [
    { to: "/my-actions", label: "My Actions", icon: ListChecks },
    { to: "/all-actions", label: "All Actions", icon: ListBullets },
  ] },
  { type: "group", label: "Sales", icon: Receipt, items: [
    { to: "/transactions", label: "Somcore PO", icon: Receipt },
    { to: "/ops-requests", label: "Operations Hub Requests", icon: ClipboardText, badge: "ops" },
    { to: "/client-pos", label: "Client POs", icon: ClipboardText },
    { to: "/client-invoices", label: "Client Invoices", icon: Receipt },
    { to: "/authorization-log", label: "Authorization Log", icon: ListChecks, badge: "auth" },
    { to: "/clients", label: "Clients", icon: UsersThree },
    { to: "/suppliers", label: "Suppliers", icon: Truck },
  ] },
  { type: "group", label: "Receivables & Payables", icon: Money, items: [
    { to: "/payables", label: "Payables Due", icon: Money },
    { to: "/payment-schedule", label: "Payment Schedule", icon: CalendarBlank, paymentScheduleAccess: true },
    { to: "/expenses", label: "Expenses", icon: CurrencyCircleDollar },
    { to: "/expense-reports", label: "Expense Reports", icon: ListChecks },
    { to: "/allocations", label: "Allocations", icon: LinkSimple },
    { to: "/advances", label: "Prepayments & Advances", icon: HandCoins },
    { to: "/aging", label: "Aged Analysis", icon: ClockCountdown },
    { to: "/statements", label: "Statements", icon: FileText },
    { to: "/wht", label: "Withholding Tax", icon: Percent },
  ] },
  { type: "group", label: "Accounting", icon: ChartBar, items: [
    { to: "/vat", label: "VAT", icon: Percent },
    { to: "/bank", label: "Bank Reconciliation", icon: Bank },
    { to: "/cash-position", label: "Cash Position", icon: Wallet },
    { to: "/cash-confirmations", label: "Cash Confirmation", icon: ListChecks },
    { to: "/cards", label: "Cards & Reconciliation", icon: Wallet },
    { to: "/cash-requests", label: "Cash Requests", icon: Lock, cashApprover: true },
    { to: "/gl", label: "General Ledger", icon: ChartBar },
    { to: "/daybook", label: "Day Book", icon: BookOpen },
    { to: "/opening-balances", label: "Opening Balances", icon: Scales },
    { to: "/periods", label: "Period Lock & Close", icon: Lock },
    { to: "/currencies", label: "Currencies", icon: Coins },
    { to: "/assets", label: "Asset Register", icon: Package },
    { to: "/asset-lodgment", label: "Asset Lodgment", icon: NotePencil },
    { to: "/asset-lodgments", label: "Lodgment History", icon: ClockCountdown },
    { to: "/loans", label: "Loan Register", icon: Coins },
    { to: "/compliance", label: "Compliance (KYC)", icon: ShieldCheck, complianceOnly: true },
  ] },
  { type: "group", label: "Inventory", icon: Package, items: [
    { to: "/inventory", label: "Inventory", icon: Package },
    { to: "/shipments", label: "Shipments (GIT)", icon: Truck },
    { to: "/items", label: "Item Master", icon: Barcode },
  ] },
  { type: "item", to: "/payroll", label: "Payroll", icon: UsersFour, hideForRole: "AP" },
  { type: "group", label: "Reports", icon: FileText, items: [
    { to: "/reports", label: "All Reports", icon: FileText },
    { to: "/reports/vat-reconciliation", label: "VAT Reconciliation", icon: Percent },
    { to: "/reports/expense-efd", label: "Pending Expense EFD", icon: Warning },
    { to: "/reports/procurement-efd", label: "Pending Procurement EFD", icon: Warning },
    { to: "/reports/fulfillment", label: "Fulfillment Reports", icon: ClipboardText },
    { to: "/reports/trips", label: "Trip Reports", icon: FileText },
    { to: "/reports/forex", label: "Forex", icon: Coins },
    { to: "/reports/po-health", label: "PO Health", icon: TreeStructure },
    { to: "/reports/margin-audit", label: "Margin Audit", icon: ChartBar, execOnly: true },
    { to: "/reports/invoice-integrity", label: "Invoice Integrity", icon: ShieldCheck, execOnly: true },
    { to: "/reports/gp-variance", label: "GP Variance", icon: Scales, execOnly: true },
    { to: "/reports/pending-adjustments", label: "Pending Adjustments", icon: Lock, execOnly: true },
    { to: "/reports/end-to-end/local", label: "E2E — Local", icon: TreeStructure },
    { to: "/reports/end-to-end/import", label: "E2E — Import", icon: TreeStructure },
    { to: "/reports/task-emails", label: "Task Emails", icon: EnvelopeSimple, execOnly: true },
  ] },
  { type: "group", label: "Documents & Help", icon: Tray, items: [
    { to: "/documents", label: "Document Inbox", icon: Tray },
    { to: "/document-gallery", label: "Document Gallery", icon: FileText, ownerOnly: true },
    { to: "/finance-calendar", label: "Finance Calendar", icon: CalendarBlank },
    { to: "/disclaimers", label: "Disclaimers", icon: ShieldWarning },
    { to: "/manual", label: "User Manual", icon: BookOpen },
  ] },
  { type: "group", label: "HR", icon: UsersThree, execOnly: true, items: HR_NAV_ITEMS },
  { type: "group", label: "Settings", icon: Gear, items: [
    { to: "/settings/document-numbering", label: "Document Numbering", icon: Hash, execOnly: true },
    { to: "/settings/statement-schedule", label: "Statement Schedule", icon: CalendarBlank, execOnly: true },
    { to: "/settings/users", label: "User Access", icon: Lock, execOnly: true },
    { to: "/export", label: "Export & Backup", icon: Export, execOnly: true },
    { to: "/deleted", label: "Deleted Items", icon: Trash },
    { to: "/others", label: "Others", icon: NotePencil, ownerOnly: true },
  ] },
];

const HR_ONLY = [
  { type: "group", label: "HR", icon: UsersThree, items: HR_NAV_ITEMS },
];

const DOC_ONLY = [
  { type: "link", label: "Pending Signed GRV", icon: Package, to: "/reports/fulfillment?tab=pending-signed-grv" },
  { type: "link", label: "Pending Signed Delivery Notes", icon: Truck, to: "/reports/fulfillment?tab=pending-signed-delivery-notes" },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const ACTIVE = "bg-[#1B4383] font-semibold text-white shadow-sm dark:bg-orange-500/15 dark:text-orange-300 dark:shadow-none dark:ring-1 dark:ring-orange-400/40";
const linkClass = ({ isActive }) =>
  `mx-2 my-0.5 flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm transition-all duration-150 ${
    isActive ? ACTIVE : "text-slate-300 hover:bg-white/[0.08] hover:text-white"
  }`;

const subLinkClass = ({ isActive }) =>
  `mx-2 my-0.5 flex items-center gap-3 rounded-xl py-2 pl-8 pr-4 text-sm transition-all duration-150 ${
    isActive ? ACTIVE : "text-slate-400 hover:bg-white/[0.08] hover:text-white"
  }`;

let _catalog = null;
function usePageBlocked() {
  const { user } = useAuth();
  const [cat, setCat] = useState(_catalog);
  React.useEffect(() => { if (!_catalog) { api.get("/admin/access-catalog").then((r) => { _catalog = r.data.pages; setCat(_catalog); }).catch(() => {}); } }, []);
  return (to) => {
    if (user?.is_owner || !cat) return false;
    const pa = user?.page_access || {};
    const pg = cat.find((p) => p.routes.some((r) => r !== "/" && (to === r || to.startsWith(r + "/"))));
    return pg ? pa[pg.key] === "none" : false;
  };
}

function OpsBadge() {
  const [n, setN] = React.useState(0);
  React.useEffect(() => {
    const load = () => api.get("/ops-requests/count").then((r) => setN(r.data?.total || 0)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("ops-requests-changed", load);
    return () => { clearInterval(t); window.removeEventListener("ops-requests-changed", load); };
  }, []);
  return n > 0 ? <span className="ml-auto rounded-full bg-amber-400 px-1.5 text-[10px] font-bold text-gray-900" data-testid="nav-ops-badge">{n}</span> : null;
}

function AuthBadge() {
  const [n, setN] = useState(0);
  React.useEffect(() => { api.get("/fulfillment/pending-auth").then((r) => setN(r.data?.total || 0)).catch(() => {}); }, []);
  return n > 0 ? <span className="ml-auto rounded-full bg-amber-400 px-1.5 text-[10px] font-bold text-gray-900" data-testid="nav-auth-badge">{n}</span> : null;
}

function NavGroup({ group }) {
  const location = useLocation();
  const { user } = useAuth();
  const blocked = usePageBlocked();
  const items = group.items.filter((it) => !blocked(it.to) && (!it.complianceOnly || user?.is_owner || user?.can_access_compliance) && (!it.ownerOnly || user?.is_owner) && (!it.execOnly || user?.is_executive) && (!it.cashApprover || user?.can_post_cash) && (!it.bankBalancesAccess || user?.can_post_cash || user?.can_bank_balances) && (!it.paymentScheduleAccess || user?.is_owner || user?.can_manage_payment_schedule || user?.can_authorize_payments));
  const hasActive = items.some((it) => location.pathname.startsWith(it.to));
  const [open, setOpen] = useState(hasActive);

  React.useEffect(() => {
    if (hasActive) setOpen(true);
  }, [hasActive]);

  if (items.length === 0) return null;

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`navgroup-${slug(group.label)}`}
        className={`mx-2 my-0.5 flex w-[calc(100%-1rem)] items-center gap-3 rounded-xl px-4 py-2.5 text-sm transition-all duration-150 ${
          hasActive
            ? "bg-white/[0.06] font-semibold text-white dark:text-orange-300"
            : "text-white/85 hover:bg-white/[0.12] hover:text-white"
        }`}
      >
        <group.icon size={18} weight="regular" />
        <span className="flex-1 text-left">{group.label}</span>
        {!open && items.some((it) => it.badge === "ops") && <OpsBadge />}
        <CaretDown size={14} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-0.5 pb-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={`nav-${slug(it.label)}`}
              className={subLinkClass}
            >
              <it.icon size={16} weight="regular" />
              {it.label}
              {it.badge === "ops" && <OpsBadge />}
              {it.badge === "auth" && <AuthBadge />}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  return (
    <button data-testid="theme-toggle" onClick={toggle} title={theme === "dark" ? "Light mode" : "Dark mode"}
      className="flex h-9 w-9 items-center justify-center rounded-md bg-white/[0.05] text-gray-300 transition-colors hover:bg-white/[0.1] hover:text-white">
      {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
    </button>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { setMenuOpen(false); }, [pathname]);

  return (
    <div className="flex min-h-screen bg-[#F4F5F7] dark:bg-[#0b1220]">
      {menuOpen && <div className="fixed inset-0 z-30 bg-black/40 backdrop-blur-[2px] lg:hidden" onClick={() => setMenuOpen(false)} data-testid="sidebar-backdrop" />}
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 flex w-60 flex-col border-r border-white/[0.12] bg-[#122E5C] text-white/85 transition-transform duration-200 ease-out dark:border-[#243049] dark:bg-[#0b1220] lg:translate-x-0 ${menuOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full"}`} data-testid="finance-sidebar">
        <div className="flex h-16 items-center justify-between border-b border-white/[0.12] px-5">
          <div className="flex flex-col justify-center gap-1" data-testid="finance-sidebar-brand">
            <img src="/somcore-logo-white.png" alt="Somcore logo" className="h-5 w-auto self-start object-contain" />
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/60">Finance</div>
          </div>
          <button onClick={() => setMenuOpen(false)} className="rounded-md p-1.5 text-white/70 hover:bg-white/10 hover:text-white lg:hidden" aria-label="Close menu" data-testid="sidebar-close"><X size={20} /></button>
        </div>
        <nav className="flex-1 overflow-y-auto py-3">
          {(user?.role_code === "HR" ? HR_ONLY : user?.role_code === "documentation" ? DOC_ONLY : GROUPS).map((entry) =>
            (entry.execOnly && !user?.is_executive) || (entry.ownerOnly && !user?.is_owner) || (entry.hideForRole && user?.role_code === entry.hideForRole) ? null : entry.type === "group" ? (
              <NavGroup key={entry.label} group={entry} />
            ) : (
              <NavLink
                key={entry.to}
                to={entry.to}
                end={entry.end}
                data-testid={`nav-${slug(entry.label)}`}
                className={linkClass}
              >
                <entry.icon size={18} weight="regular" />
                {entry.label}
              </NavLink>
            )
          )}
          {user?.is_executive && (
            <NavLink
              to="/executive"
              data-testid="nav-executive"
              className={({ isActive }) =>
                `mx-2 my-0.5 mt-2 flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm transition-colors ${
                  isActive
                    ? "bg-[#1B4383] font-semibold text-white shadow-sm dark:shadow-none"
                    : "text-[#E6C78F] hover:bg-white/[0.08] hover:text-white"
                }`
              }
            >
              <Crown size={18} weight="fill" />
              Executive
            </NavLink>
          )}
        </nav>
        <div className="border-t border-white/[0.12] bg-black/10 p-4">
          <div className="mb-2">
            <NotificationsBell />
          </div>
          <div className="mb-2 flex items-center gap-2 rounded-lg bg-white/[0.04] p-1.5">
            <NavLink to="/profile" data-testid="nav-profile" className="flex flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-xs transition-colors hover:bg-white/[0.06]">
              <UserCircle size={20} className="text-gray-400" />
              <span className="min-w-0">
                <span className="block truncate font-medium text-white">{user?.name}</span>
                <span className="block truncate text-white/60">{user?.role}</span>
                {user?.read_only && <span className="mt-0.5 inline-block rounded-sm bg-amber-400 px-1.5 text-[10px] font-bold text-gray-900" data-testid="readonly-badge">Read-only</span>}
              </span>
            </NavLink>
            <ThemeToggle />
          </div>
          <a href="/" data-testid="finance-back-to-ops"
            onClick={(e) => { if (!window.confirm("Leave Finance Hub And Go Back To Operations Hub?")) e.preventDefault(); }}
            className="mb-2 flex w-full items-center gap-2 rounded-md border border-white/20 px-3 py-2 text-sm font-medium text-white/90 transition-colors hover:bg-white/[0.10] hover:text-white">
            <SquaresFour size={16} /> Back To Operations Hub
          </a>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="flex w-full items-center gap-2 rounded-md bg-white/[0.08] px-3 py-2 text-sm text-white/85 transition-colors hover:bg-white/[0.16] hover:text-white"
          >
            <SignOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {/* Content */}
      <div className={`lx-content min-w-0 flex-1 lg:ml-60 ${user?.read_only ? "lx-readonly" : ""}`}>
        <div className="flex h-12 items-center gap-3 bg-[#122E5C] px-3 text-white dark:bg-[#0b1220] lg:hidden" data-testid="finance-mobile-bar">
          <button onClick={() => setMenuOpen(true)} className="rounded-md p-1.5 hover:bg-white/10" aria-label="Open menu" data-testid="sidebar-open"><List size={22} /></button>
          <img src="/somcore-logo-white.png" alt="Somcore logo" className="h-4 w-auto object-contain" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/60">Finance</span>
        </div>
        {user?.read_only && <ReadOnlyGuard />}
        {user?.must_change_password && <ForcePasswordChange />}
        {children}
      </div>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="sticky top-0 z-20 flex min-h-16 flex-wrap items-center justify-between gap-2 border-b border-gray-200 bg-white/90 px-4 py-2 backdrop-blur-md sm:px-8 dark:border-[#243049] dark:bg-[#0b1220]/95 dark:text-gray-100">
      <div className="min-w-0">
        <h1 className="font-display text-lg font-bold text-gray-900 sm:text-xl dark:text-white">{title}</h1>
        {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2">{actions}</div>
    </div>
  );
}
